import React, { useState, useEffect } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { InlineList, InlineListRow } from '@/components/InlineList';
import type { AdminDayResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './AdminDays.css';
import '../styles/common.css';

const AdminDays: React.FC = () => {
  const [days, setDays] = useState<AdminDayResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [moduleName, setModuleName] = useState<string>('');
  const [className, setClassName] = useState<string>('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const moduleId = searchParams.get('moduleId');
  const classId = searchParams.get('classId');
  const pageTitle = [moduleName, className].filter(Boolean).join(' - ');

  useEffect(() => {
    fetchUserInfo();
    if (moduleId && classId) {
      fetchDays();
      fetchNames();
    } else {
      setError('Module ID and Class ID are required');
      setLoading(false);
    }
  }, [moduleId, classId]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchNames = async () => {
    if (!moduleId || !classId) return;
    const [mName, cName] = await Promise.all([
      adminService.getModuleNameById(moduleId),
      adminService.getClassNameById(classId),
    ]);
    setModuleName(mName);
    setClassName(cName);
  };

  const fetchDays = async () => {
    if (!moduleId || !classId) return;
    
    try {
      setLoading(true);
      setError(null);
      const daysData = await adminService.getDaysByModule(moduleId, classId);
      setDays(daysData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch days');
    } finally {
      setLoading(false);
    }
  };


  const handleViewDay = (date: string) => {
    const dateOnly = date.substring(0, 10);
    const query = new URLSearchParams({ moduleId: moduleId || '', date: dateOnly, classId: classId || '' });
    navigate(`/admin/day-conversations?${query}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const weekday = date.toLocaleString('en-US', { weekday: 'long' });
    return `${weekday}, ${month} ${day}, ${year}`;
  };

  return (
    <div className="admin-days-container">
      <AppHeader 
        user={user} 
        title="KI Expert - Admin" 
        subtitle={pageTitle ? `${pageTitle} - Conversations by day` : 'Conversations by day'} 
      />
      
      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              { label: 'Modules', path: '/admin/modules' },
              { label: pageTitle ? `${pageTitle} - Conversations by day` : 'Conversations by day' },
            ]} />
            <h2 className="navigation-bar-title">{pageTitle ? `${pageTitle} - Conversations by day` : 'Conversations by day'}</h2>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading days...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchDays} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : days.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">📅</div>
            <h3>No conversations found</h3>
            <p>There are no conversations in this module yet.</p>
          </div>
        ) : (
          <InlineList compact>
            {days.map(day => (
              <InlineListRow key={day.date} onClick={() => handleViewDay(day.date)}>
                <h3 className="admin-day-title">{formatDate(day.date)}</h3>
                <span className="admin-day-count">{day.conversationCount} conversation{day.conversationCount !== 1 ? 's' : ''}</span>
              </InlineListRow>
            ))}
          </InlineList>
        )}
      </main>
    </div>
  );
};

export default AdminDays;
